import type {
  AncillariesForReservation,
  AncillarySelection,
  AncillaryElectronicMiscellaneousDocumentType,
} from 'api/flights'

// Canonical EMD-A value for checked bags sold with the ticket. Validation ignores it
// (schema-enum only) and svc-flights-sabre persists it verbatim — see the `/select`
// `ancillaries` contract (baggage.ts, point (a)).
const BAGGAGE_EMD_TYPE: AncillaryElectronicMiscellaneousDocumentType = 'FLIGHT_COUPON_ASSOCIATED'
// Literal ancillary group code for baggage, per the `/select` contract (point (a)).
const BAGGAGE_ANCILLARY_GROUP_CODE = 'BG' as const

/** The parts of a leg needed to find its availability entry. */
export interface BaggageAvailabilityLegRef {
  /** The leg's own id, as sent in `selectedFareIds` on the availability request. */
  selectedFareId: string
  /** Every flight of the leg, in itinerary order. */
  flightIds: Array<string>
}

/**
 * Resolves the availability entry for a leg.
 *
 * Checked baggage is priced per leg: one offer covers every flight of a through-checked leg at one
 * price. Two independent handles identify that offer, and both are needed:
 *
 * 1. `selectedFareId` — what availability stamps on each offer. Exact, and stable across responses,
 *    so a saved selection re-resolves after a re-fetch.
 * 2. `flightIds` — any flight of the leg. The fallback for a response served from a cache written
 *    before the leg key existed, which names a flight instead. It intersects rather than comparing
 *    the leg's first flight, because the flight named is not guaranteed to be the first one.
 *
 * Matching on route (`origin`/`destination`) is deliberately not a fallback: it breaks on open-jaw
 * and on any multi-city itinerary that flies the same city pair twice.
 */
export function findBaggageAvailabilityLeg(
  availability: App.FlightsBaggageAvailability,
  leg: BaggageAvailabilityLegRef,
): App.FlightsBaggageAvailabilityLeg | undefined {
  const byLegId = availability.legs.find(
    entry => entry.isLegKeyed && entry.legId === leg.selectedFareId,
  )
  if (byLegId) { return byLegId }

  return availability.legs.find(
    entry => entry.flightIds.some(flightId => leg.flightIds.includes(flightId)),
  )
}

/**
 * Builds the `AncillariesForReservation` payload sent to `POST /api/flights/select`
 * (`additionalPayload.ancillaries`) from the persisted baggage selections + the current
 * availability. Pure (no redux) so both the modal-resolve path and the refresh
 * recovery path can reuse it.
 *
 * Satisfies the `/select` baggage validation contract:
 * - `ancillaryOfferId` = availability `offerId`.
 * - selections grouped under `ancillariesByLeg`, keyed by the leg's `selectedFareId`. When the
 *   availability response predates the leg key it names flights instead, and the deprecated
 *   `ancillariesByFlight` is sent instead so the request's shape follows the response's own. Never
 *   both: svc-flights rejects that rather than guessing which of two disagreeing sets to charge for.
 * - each line's `basePriceAmount` = the offer BASE amount.
 * - `totalCostAmount` = Σ offer TOTAL amount × quantity; base and total differ
 *   when the fare is tax-inclusive, so the top-level total uses `amount` while each line
 *   uses `basePriceAmount`.
 *
 * Returns `undefined` when no selection resolves to a live availability option (e.g. every
 * saved option expired), so the caller can re-price without ancillaries to zero the fee.
 */
export function buildBaggageAncillaries(
  selections: Record<string, Record<string, App.FlightBaggageSelection>>,
  availability: App.FlightsBaggageAvailability,
): AncillariesForReservation | undefined {
  // legId -> travellerId -> optionId -> option, for a stable verbatim price lookup.
  const optionLookup = new Map<string, Map<string, Map<string, App.FlightsBaggageAvailabilityOption>>>()
  const legsById = new Map<string, App.FlightsBaggageAvailabilityLeg>()
  for (const leg of availability.legs) {
    legsById.set(leg.legId, leg)
    const travellerMap = new Map<string, Map<string, App.FlightsBaggageAvailabilityOption>>()
    for (const traveller of leg.travellers) {
      travellerMap.set(
        traveller.travellerId,
        new Map(traveller.options.map(option => [option.id, option])),
      )
    }
    optionLookup.set(leg.legId, travellerMap)
  }

  // Lines are collected against the availability leg first, then emitted under whichever of the two
  // groupings that leg supports, because the choice has to be uniform across the whole payload.
  const linesByLeg = new Map<App.FlightsBaggageAvailabilityLeg, Array<AncillarySelection>>()
  let totalCostAmount = 0

  // selections is nested per leg: selectedFareId -> travellerId -> selection. A traveller may hold
  // a selection on more than one leg, so iterate both levels and group by the leg.
  for (const travellerSelections of Object.values(selections)) {
    for (const [travellerId, selection] of Object.entries(travellerSelections)) {
      const leg = legsById.get(selection.availabilityLegId)
      const option = optionLookup
        .get(selection.availabilityLegId)?.get(travellerId)?.get(selection.optionId)
      if (!leg || !option) { continue }

      const line: AncillarySelection = {
        travellerId,
        selectedFareId: selection.selectedFareId,
        quantity: 1,
        ancillarySubCode: option.subCode,
        airlineCode: option.airline,
        ancillaryGroupCode: BAGGAGE_ANCILLARY_GROUP_CODE,
        electronicMiscellaneousDocumentType: BAGGAGE_EMD_TYPE,
        basePriceAmount: option.basePriceAmount,
      }

      const existing = linesByLeg.get(leg) ?? []
      existing.push(line)
      linesByLeg.set(leg, existing)
      totalCostAmount += option.amount * line.quantity
    }
  }

  if (linesByLeg.size === 0) {
    return undefined
  }

  const base = {
    ancillaryOfferId: availability.offerId,
    totalCostAmount,
    currency: availability.currency,
  }

  // A single offer without a leg key downgrades the whole payload: the flight-keyed grouping is
  // accepted for every leg (any flight of a leg resolves it), whereas the leg-keyed one is rejected
  // outright for a key that is not one of the selected fares.
  const isEveryLegKeyed = [...linesByLeg.keys()].every(leg => leg.isLegKeyed)
  if (!isEveryLegKeyed) {
    const ancillariesByFlight: { [flightId: string]: Array<AncillarySelection> } = {}
    for (const [leg, lines] of linesByLeg) {
      ancillariesByFlight[leg.flightIds[0] ?? leg.legId] = lines
    }
    return { ...base, ancillariesByFlight }
  }

  const ancillariesByLeg: { [legId: string]: Array<AncillarySelection> } = {}
  for (const [leg, lines] of linesByLeg) {
    ancillariesByLeg[leg.legId] = lines
  }
  return { ...base, ancillariesByLeg }
}
